import {
  CONFIG_FIELD_NAME_ID,
  CONFIG_CLASS_ID,
  TConfigFieldNameId,
  LS_ADDITIONAL_SERVICE_TYPE
} from '../types/product.configuration.types';
import { TbordOneToneIcon } from '../components/NudosComponents/BordOneToneIcon/BordOneToneIcon.types';

export const CONFIG_FIELD_TYPE = {
  TEXT: 'text',
  NUMBER: 'number',
  PASSWORD: 'password',
  EMAIL: 'email',
  TEL: 'tel',
  TEXTAREA: 'textarea',
  FILE: 'file'
} as const;

export type TConfigFieldType = typeof CONFIG_FIELD_TYPE[keyof typeof CONFIG_FIELD_TYPE];

export interface IConfigField {
  id: TConfigFieldNameId;
  isRequired: boolean;
  fieldType: TConfigFieldType;
  label: string;
  placeholder: string;
  fakeLabel?: boolean;
}

export interface IConfigFieldGridPosition {
  row: number;
  col: number;
  span: number;
}

export interface IConfigClass {
  id: CONFIG_CLASS_ID;
  name: string;
  variantIcon: TbordOneToneIcon | null;
  fields?: readonly IConfigField[];
  gridPositions?: Partial<Record<TConfigFieldNameId, IConfigFieldGridPosition>>;
  alert?: {
    title: string;
    type: 'warning' | 'time' | 'bulb' | 'information';
  };
}

export const CONFIG_CLASS: Record<CONFIG_CLASS_ID, IConfigClass> = {
  [CONFIG_CLASS_ID.DEP]: {
    id: CONFIG_CLASS_ID.DEP,
    name: 'DEP',
    variantIcon: 'laptopSettings',
    fields: [
      {
        id: CONFIG_FIELD_NAME_ID.COMPANY_APPLE_ID,
        isRequired: true,
        fieldType: CONFIG_FIELD_TYPE.TEXT,
        label: 'Company Apple ID',
        placeholder: 'Escribe el ID'
      },
      {
        id: CONFIG_FIELD_NAME_ID.EMAIL_APPLE_ACCOUNT,
        isRequired: true,
        fieldType: CONFIG_FIELD_TYPE.EMAIL,
        label: 'Correo Apple Account',
        placeholder: 'Escribe el correo'
      },
      {
        id: CONFIG_FIELD_NAME_ID.COMPANY_NAME,
        isRequired: true,
        fieldType: CONFIG_FIELD_TYPE.TEXT,
        label: 'Nombre de la empresa',
        placeholder: 'Escribe el nombre'
      }
    ],
    gridPositions: {
      [CONFIG_FIELD_NAME_ID.COMPANY_APPLE_ID]: { row: 1, col: 1, span: 1 },
      [CONFIG_FIELD_NAME_ID.EMAIL_APPLE_ACCOUNT]: { row: 1, col: 2, span: 1 },
      [CONFIG_FIELD_NAME_ID.COMPANY_NAME]: { row: 2, col: 1, span: 2 }
    }
  },
  [CONFIG_CLASS_ID.APPLE_CARE_PLUS]: {
    id: CONFIG_CLASS_ID.APPLE_CARE_PLUS,
    name: 'Apple Care+',
    variantIcon: 'apple',
    fields: [
      {
        id: CONFIG_FIELD_NAME_ID.COMMENTS,
        isRequired: false,
        label: 'Comentarios',
        placeholder: 'Escribe detalles de la configuración',
        fieldType: CONFIG_FIELD_TYPE.TEXTAREA
      }
    ],
    gridPositions: {
      [CONFIG_FIELD_NAME_ID.COMMENTS]: { row: 1, col: 1, span: 2 }
    }
  },
  [CONFIG_CLASS_ID.MDM_ENROLLMENT]: {
    id: CONFIG_CLASS_ID.MDM_ENROLLMENT,
    name: 'MDM Enrollment',
    variantIcon: 'cpu',
    fields: [
      {
        id: CONFIG_FIELD_NAME_ID.COMMENTS,
        isRequired: true,
        label: 'Comentarios',
        placeholder: `1. Nombre del perfil: Configuración de MDM para dispositivos móviles.\n2.Tipo de dispositivo: iOS.\n3. Requisitos de inscripción: Los usuarios deben tener una cuenta corporativa y el dispositivo debe estar conectado a Wi-Fi.\n4. Configuración de seguridad: Se debe establecer un código de acceso de al menos 6 dígitos y habilitar el cifrado del dispositivo.\n5. Aplicaciones obligatorias: Instalar las aplicaciones de productividad como correo electrónico, calendario y herramientas de colaboración.\n6. Políticas de uso: Los usuarios deben aceptar las políticas de uso antes de completar la inscripción.\n7. Soporte técnico: Proporcionar un enlace a la página de soporte para asistencia adicional.`,
        fieldType: CONFIG_FIELD_TYPE.TEXTAREA
      },
      {
        id: CONFIG_FIELD_NAME_ID.FILE_URL,
        isRequired: false,
        label: '',
        placeholder: 'PDF o JPG (Opcional)',
        fieldType: CONFIG_FIELD_TYPE.FILE,
        fakeLabel: false
      }
    ],
    gridPositions: {
      [CONFIG_FIELD_NAME_ID.COMMENTS]: { row: 1, col: 1, span: 2 },
      [CONFIG_FIELD_NAME_ID.FILE_URL]: { row: 2, col: 1, span: 2 }
    }
  },
  [CONFIG_CLASS_ID.APPLE_CONFIGURATOR]: {
    id: CONFIG_CLASS_ID.APPLE_CONFIGURATOR,
    name: 'Apple configurator',
    variantIcon: 'apple',
    fields: [
      {
        id: CONFIG_FIELD_NAME_ID.APPLE_CONFIGURATOR_USER,
        isRequired: true,
        label: 'Usuario',
        placeholder: 'Escribe el usuario',
        fieldType: CONFIG_FIELD_TYPE.TEXT
      },
      {
        id: CONFIG_FIELD_NAME_ID.APPLE_CONFIGURATOR_PASSWORD,
        isRequired: true,
        label: 'Contraseña',
        placeholder: 'Escribe la contraseña',
        fieldType: CONFIG_FIELD_TYPE.TEXT
      },
      {
        id: CONFIG_FIELD_NAME_ID.APPLE_CONFIGURATOR_PHONE,
        isRequired: true,
        label: 'Número de teléfono para SMS',
        placeholder: 'Telefono de a quien solicitarle el SMS',
        fieldType: CONFIG_FIELD_TYPE.TEL
      }
    ],
    gridPositions: {
      [CONFIG_FIELD_NAME_ID.APPLE_CONFIGURATOR_USER]: { row: 1, col: 1, span: 1 },
      [CONFIG_FIELD_NAME_ID.APPLE_CONFIGURATOR_PASSWORD]: { row: 1, col: 2, span: 1 },
      [CONFIG_FIELD_NAME_ID.APPLE_CONFIGURATOR_PHONE]: { row: 2, col: 1, span: 2 }
    }
  },
  [CONFIG_CLASS_ID.HASH_ID]: {
    id: CONFIG_CLASS_ID.HASH_ID,
    name: 'Hash ID',
    variantIcon: 'hash',
    fields: [
      {
        id: CONFIG_FIELD_NAME_ID.COMMENTS,
        isRequired: false,
        label: 'Comentarios',
        placeholder: 'Escribe detalles de la configuración',
        fieldType: CONFIG_FIELD_TYPE.TEXTAREA
      }
    ],
    alert: {
      title:
        'Cuando se complete la configuración, el cliente debe confirmar el Enrollment de la herramienta para continuar con la orden',
      type: 'warning'
    },
    gridPositions: {
      [CONFIG_FIELD_NAME_ID.COMMENTS]: { row: 1, col: 1, span: 2 }
    }
  },
  [CONFIG_CLASS_ID.CREAR_ADMIN]: {
    id: CONFIG_CLASS_ID.CREAR_ADMIN,
    name: 'Creación de Admin',
    variantIcon: 'atSign',
    fields: [
      {
        id: CONFIG_FIELD_NAME_ID.ADMIN_NAME,
        isRequired: true,
        label: 'Usuario',
        placeholder: 'Escribe el usuario',
        fieldType: CONFIG_FIELD_TYPE.TEXT
      },
      {
        id: CONFIG_FIELD_NAME_ID.ADMIN_PASSWORD,
        isRequired: true,
        label: 'Contraseña',
        placeholder: 'Escribe la contraseña',
        fieldType: CONFIG_FIELD_TYPE.TEXT
      },
      {
        id: CONFIG_FIELD_NAME_ID.ASK_QUESTIONS,
        isRequired: true,
        label: 'Preguntas frecuentes',
        placeholder: `Pregunta: ¿Cuál es el nombre de tu primera mascota? \nRespuesta: Mi primera mascota se llamaba Rocky.`,
        fieldType: 'textarea'
      }
    ],
    gridPositions: {
      [CONFIG_FIELD_NAME_ID.ADMIN_NAME]: { row: 1, col: 1, span: 1 },
      [CONFIG_FIELD_NAME_ID.ADMIN_PASSWORD]: { row: 1, col: 2, span: 1 },
      [CONFIG_FIELD_NAME_ID.ASK_QUESTIONS]: { row: 2, col: 1, span: 2 }
    }
  },
  [CONFIG_CLASS_ID.ENCRIPTAR_BITLOCKER]: {
    id: CONFIG_CLASS_ID.ENCRIPTAR_BITLOCKER,
    name: 'Bitlocker',
    variantIcon: 'sliders',
    fields: [
      {
        id: CONFIG_FIELD_NAME_ID.COMMENTS,
        isRequired: false,
        label: 'Comentarios',
        placeholder: 'Escribe los comentarios',
        fieldType: CONFIG_FIELD_TYPE.TEXTAREA
      }
    ],
    gridPositions: {
      [CONFIG_FIELD_NAME_ID.COMMENTS]: { row: 1, col: 1, span: 2 }
    }
  },
  [CONFIG_CLASS_ID.CONFIG_AVANZADA]: {
    id: CONFIG_CLASS_ID.CONFIG_AVANZADA,
    name: 'Config. Avanzada',
    variantIcon: 'star',
    fields: [
      {
        id: CONFIG_FIELD_NAME_ID.COMMENTS,
        isRequired: true,
        label: 'Comentarios',
        placeholder: 'Escribe detalles de la configuración',
        fieldType: CONFIG_FIELD_TYPE.TEXTAREA
      },
      {
        id: CONFIG_FIELD_NAME_ID.FILE_URL,
        isRequired: false,
        label: '',
        placeholder: 'PDF o JPG (Opcional)',
        fieldType: CONFIG_FIELD_TYPE.FILE,
        fakeLabel: false
      }
    ],
    gridPositions: {
      [CONFIG_FIELD_NAME_ID.COMMENTS]: { row: 1, col: 1, span: 2 },
      [CONFIG_FIELD_NAME_ID.FILE_URL]: { row: 2, col: 1, span: 2 }
    }
  },
  [CONFIG_CLASS_ID.WIPING]: {
    id: CONFIG_CLASS_ID.WIPING,
    name: LS_ADDITIONAL_SERVICE_TYPE.WIPING,
    variantIcon: null
  },
  [CONFIG_CLASS_ID.GARANTIA_1_ANO]: {
    id: CONFIG_CLASS_ID.GARANTIA_1_ANO,
    name: 'Garantía 1 año',
    variantIcon: 'shield',
    fields: [
      {
        id: CONFIG_FIELD_NAME_ID.COMMENTS,
        isRequired: false,
        label: 'Comentarios',
        placeholder: 'Escribe detalles de la configuración',
        fieldType: CONFIG_FIELD_TYPE.TEXTAREA
      }
    ],
    gridPositions: {
      [CONFIG_FIELD_NAME_ID.COMMENTS]: { row: 1, col: 1, span: 2 }
    }
  },
  [CONFIG_CLASS_ID.GARANTIA_3_ANOS]: {
    id: CONFIG_CLASS_ID.GARANTIA_3_ANOS,
    name: 'Garantía 3 años',
    variantIcon: 'shield',
    fields: [
      {
        id: CONFIG_FIELD_NAME_ID.COMMENTS,
        isRequired: false,
        label: 'Comentarios',
        placeholder: 'Escribe detalles de la configuración',
        fieldType: CONFIG_FIELD_TYPE.TEXTAREA
      }
    ],
    gridPositions: {
      [CONFIG_FIELD_NAME_ID.COMMENTS]: { row: 1, col: 1, span: 2 }
    }
  },
  [CONFIG_CLASS_ID.DIAGNOSTICO]: {
    id: CONFIG_CLASS_ID.DIAGNOSTICO,
    name: 'Diagnóstico',
    variantIcon: null
  },
  [CONFIG_CLASS_ID.BASIC_CONFIGURATION]: {
    id: CONFIG_CLASS_ID.BASIC_CONFIGURATION,
    name: 'Conf. básica',
    variantIcon: null
  },
  [CONFIG_CLASS_ID.WIPING_BLANCO]: {
    id: CONFIG_CLASS_ID.WIPING_BLANCO,
    name: LS_ADDITIONAL_SERVICE_TYPE.WIPING,
    variantIcon: null
  }
} as const;

export const CONFIG_FIELD_NAME_TO_ID = {
  Comments: CONFIG_FIELD_NAME_ID.COMMENTS,
  FileUrl: CONFIG_FIELD_NAME_ID.FILE_URL,
  CompanyAppleID: CONFIG_FIELD_NAME_ID.COMPANY_APPLE_ID,
  EmailAppleAccount: CONFIG_FIELD_NAME_ID.EMAIL_APPLE_ACCOUNT,
  CompanyName: CONFIG_FIELD_NAME_ID.COMPANY_NAME,
  AppleConfiguratorUser: CONFIG_FIELD_NAME_ID.APPLE_CONFIGURATOR_USER,
  AppleConfiguratorPassword: CONFIG_FIELD_NAME_ID.APPLE_CONFIGURATOR_PASSWORD,
  AppleConfiguratorPhone: CONFIG_FIELD_NAME_ID.APPLE_CONFIGURATOR_PHONE,
  AdminName: CONFIG_FIELD_NAME_ID.ADMIN_NAME,
  AdminPassword: CONFIG_FIELD_NAME_ID.ADMIN_PASSWORD,
  AskQuestions: CONFIG_FIELD_NAME_ID.ASK_QUESTIONS
} as const;
export type TConfigFieldNameToId = keyof typeof CONFIG_FIELD_NAME_TO_ID;

export const CONFIG_CLASS_NAME_TO_ID = {
  DEP: CONFIG_CLASS_ID.DEP,
  'AppleCare+': CONFIG_CLASS_ID.APPLE_CARE_PLUS,
  'MDM enrollment': CONFIG_CLASS_ID.MDM_ENROLLMENT,
  'Apple configurator': CONFIG_CLASS_ID.APPLE_CONFIGURATOR,
  'Hash ID': CONFIG_CLASS_ID.HASH_ID,
  'Crear Admin': CONFIG_CLASS_ID.CREAR_ADMIN,
  'Encriptar Bitlocker': CONFIG_CLASS_ID.ENCRIPTAR_BITLOCKER,
  'Configuración Avanzada': CONFIG_CLASS_ID.CONFIG_AVANZADA,
  Wiping: CONFIG_CLASS_ID.WIPING,
  'Garantía Contra Accidentes 1 año': CONFIG_CLASS_ID.GARANTIA_1_ANO,
  'Garantía Extendida a 3 años': CONFIG_CLASS_ID.GARANTIA_3_ANOS,
  Diagnóstico: CONFIG_CLASS_ID.DIAGNOSTICO
} as const;
export type TConfigClassNameToId = keyof typeof CONFIG_CLASS_NAME_TO_ID;

export const CONFIG_CLASS_REQUIRED_ADDITIONAL_ACTIONS = [
  CONFIG_CLASS_ID.APPLE_CONFIGURATOR,
  CONFIG_CLASS_ID.HASH_ID,
  CONFIG_CLASS_ID.DEP
] as const;

export type TConfigClassRequiredAdditionalActions = typeof CONFIG_CLASS_REQUIRED_ADDITIONAL_ACTIONS[number];

export const TRANSLATED_CONFIGURATION_CLASS_NAMES: Record<CONFIG_CLASS_ID, string> = {
  [CONFIG_CLASS_ID.DEP]: 'dep',
  [CONFIG_CLASS_ID.APPLE_CARE_PLUS]: 'appleCarePlus',
  [CONFIG_CLASS_ID.MDM_ENROLLMENT]: 'mdmEnrollment',
  [CONFIG_CLASS_ID.APPLE_CONFIGURATOR]: 'appleConfigurator',
  [CONFIG_CLASS_ID.HASH_ID]: 'hashId',
  [CONFIG_CLASS_ID.CREAR_ADMIN]: 'createAdmin',
  [CONFIG_CLASS_ID.ENCRIPTAR_BITLOCKER]: 'encryptBitlocker',
  [CONFIG_CLASS_ID.CONFIG_AVANZADA]: 'advancedConfiguration',
  [CONFIG_CLASS_ID.WIPING]: 'wiping',
  [CONFIG_CLASS_ID.GARANTIA_1_ANO]: 'accidentalWarranty1Year',
  [CONFIG_CLASS_ID.GARANTIA_3_ANOS]: 'extendedWarranty3Years',
  [CONFIG_CLASS_ID.DIAGNOSTICO]: 'diagnostic',
  [CONFIG_CLASS_ID.BASIC_CONFIGURATION]: 'basicConfiguration',
  [CONFIG_CLASS_ID.WIPING_BLANCO]: 'wipingWhite'
} as const;

export const CONFIGURATION_FIELDS_NAME = {
  Comments: 'Comments',
  FileUrl: 'FileUrl',
  CompanyAppleID: 'CompanyAppleID',
  EmailAppleAccount: 'EmailAppleAccount',
  CompanyName: 'CompanyName',
  AppleConfiguratorUser: 'AppleConfiguratorUser',
  AppleConfiguratorPassword: 'AppleConfiguratorPassword',
  AppleConfiguratorPhone: 'AppleConfiguratorPhone',
  AdminName: 'AdminName',
  AdminPassword: 'AdminPassword',
  AskQuestions: 'AskQuestions'
} as const;

export type TConfigFieldName = typeof CONFIGURATION_FIELDS_NAME[keyof typeof CONFIGURATION_FIELDS_NAME];

export const TRANSLATED_CONFIGURATION_FIELD_NAMES: Record<TConfigFieldName, string> = {
  [CONFIGURATION_FIELDS_NAME.Comments]: 'comments',
  [CONFIGURATION_FIELDS_NAME.FileUrl]: 'fileUrl',
  [CONFIGURATION_FIELDS_NAME.CompanyAppleID]: 'companyAppleId',
  [CONFIGURATION_FIELDS_NAME.EmailAppleAccount]: 'emailAppleAccount',
  [CONFIGURATION_FIELDS_NAME.CompanyName]: 'companyName',
  [CONFIGURATION_FIELDS_NAME.AppleConfiguratorUser]: 'appleConfiguratorUser',
  [CONFIGURATION_FIELDS_NAME.AppleConfiguratorPassword]: 'appleConfiguratorPassword',
  [CONFIGURATION_FIELDS_NAME.AppleConfiguratorPhone]: 'appleConfiguratorPhone',
  [CONFIGURATION_FIELDS_NAME.AdminName]: 'adminName',
  [CONFIGURATION_FIELDS_NAME.AdminPassword]: 'adminPassword',
  [CONFIGURATION_FIELDS_NAME.AskQuestions]: 'askQuestions'
} as const;
